import { faStar as farStar } from '@fortawesome/pro-regular-svg-icons';
import { faStar as fasStar } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import {
    BZ2_count_VIEWS_FROM_created_date,
    BZ2_REFRESHED,
    BZ2_STAR_IT,
    BZ2_STARRED_NR,
    BZ2_UNSTAR_IT,
    SHOW_PRICE_HISTORY,
} from 'mk/autogenerated/translations/ProductInfo.617330d3c64a86fb30cacc5c36fad17b'
import { linebreaksbr, trim } from 'mk/bazaar/common/filters';
import { capFirst } from 'mk/common/text';
import ProductFacetList from 'mk2/apps/bazaar/components/ProductFacetList';
import ProductInfoHeader from 'mk2/apps/bazaar/components/ProductInfoHeader';
import ProductPrice from 'mk2/apps/bazaar/components/ProductPrice';
import ProductPriceHistory from 'mk2/apps/bazaar/components/ProductPriceHistory';
import ProductStockStatus from 'mk2/apps/bazaar/components/ProductStockStatus';
import { ProductEntity } from 'mk2/apps/bazaar/schemas';
import { Btn,  BtnSidePadding, BtnType } from 'mk2/components/Btn';
import { ConnectedStar } from 'mk2/containers/Star/Star';
import { timeAgo, timeAt } from 'mk2/helpers/timeformat';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { interpolate } from 'mk2/services/i18n';
import React, { useEffect, useState } from 'react';
import styles from './ProductInfo.mscss';

interface OwnProps {
    className?: string;
    product: ProductEntity;
    viewsCount: number;
}

type Props = OwnProps;

const ProductInfo: React.FunctionComponent<Props> = ({ className, product, viewsCount }) => {
    const [showPriceHistory, setShowPriceHistory] = useState<boolean>(false);

    const onTogglePriceHistory = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            setShowPriceHistory((s) => !s);
        },
        [setShowPriceHistory],
    );

    // Close prices history if product ID has changed
    useEffect(() => {
        setShowPriceHistory(false);
    }, [product.id, setShowPriceHistory]);

    const havePriceHistoryData = product.prices.length > 1;

    return (
        <div className={cx(styles.ProductInfo, className)}>
            <ProductInfoHeader product={product} />

            <div className={cx(styles.ProductInfo__body, styles[`ProductInfo__body--${product.quality}`])}>
                <div className={styles.ProductInfo__wishlist}>
                    <ConnectedStar entity="bazaar.product" id={product.id} enableKeyboardShortcut>
                        {({ isStarred, count, onStarToggle }) => (
                            <div className={styles.ProductInfo__star}>
                                <Btn
                                    classNames={{
                                        icon: cx(
                                            styles.ProductInfo__star__btn__icon,
                                            isStarred && styles['ProductInfo__star__btn__icon--on'],
                                        ),
                                    }}
                                    icon={isStarred ? fasStar : farStar}
                                    label={isStarred ? BZ2_UNSTAR_IT : BZ2_STAR_IT}
                                    type={BtnType.BlueText}
                                    sidePadding={BtnSidePadding.None}
                                    onClick={onStarToggle}
                                />

                                {count > 0 && (
                                    <span className={styles.ProductInfo__star__count}>
                                        &nbsp;- {BZ2_STARRED_NR} {count}×
                                    </span>
                                )}
                            </div>
                        )}
                    </ConnectedStar>
                </div>

                <h1 className={styles.ProductInfo__title}>{capFirst(product.title)}</h1>

                <div className={styles.ProductInfo__labels}>
                    <ProductFacetList product={product} />
                    <ProductStockStatus stockStatus={product.stockStatus} />
                </div>

                <div className={styles.ProductInfo__description}>{linebreaksbr(trim(product.description))}</div>

                {!showPriceHistory && havePriceHistoryData && (
                    <div className={styles.ProductInfo__showHistory}>
                        <Btn
                            type={BtnType.Link}
                            label={SHOW_PRICE_HISTORY}
                            onClick={onTogglePriceHistory}
                            sidePadding={BtnSidePadding.None}
                        />
                    </div>
                )}

                <div className={styles.ProductInfo__infoBottom}>
                    <div className={styles.ProductInfo__infoBottomTime}>
                        <div className={styles.ProductInfo__infoBottomActualized}>
                            {BZ2_REFRESHED + ' ' + timeAgo(product.startTime, null, true).toLowerCase()}
                        </div>

                        {viewsCount > 0 &&
                            interpolate(BZ2_count_VIEWS_FROM_created_date, {
                                count: viewsCount,
                                created_date: timeAt(product.createdTime),
                            })}
                    </div>

                    <ProductPrice
                        className={styles.ProductInfo__price}
                        price={product.price}
                        priceOld={product.priceOld}
                        fontSize="1.5x"
                    />
                </div>

                {showPriceHistory && havePriceHistoryData && (
                    <ProductPriceHistory className={styles.ProductInfo__priceHistory} prices={product.prices} />
                )}
            </div>
        </div>
    );
};

ProductInfo.displayName = 'ProductInfo';

export default React.memo<Props>(ProductInfo);
